import { useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Camera, Trash2, Upload, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { guardPreview } from '@/lib/previewMode'

/** Everything the avatar and its editor need to know about one person's photo. */
export type PhotoSource = {
  /** Cache key, e.g. "staff:<id>". */
  key: string
  name: string
  /** photoUpdatedAt from the API; null when the person has no photo. Changes whenever the photo does. */
  version: string | null
  /** The tenant has switched photos on. When false nothing is shown at all. */
  enabled: boolean
  canEdit: boolean
  notice: string | null
  load: () => Promise<Blob>
  upload: (photo: Blob) => Promise<unknown>
  /** Omit where the viewer may not delete photos (trainers can replace but not remove). */
  remove?: () => Promise<unknown>
  /** Called after a photo is saved or removed, so the caller can refresh its list. */
  onChanged?: () => void
}

const DEFAULT_NOTICE =
  'This photo is used to help confirm who completed training. It is stored securely and can be removed at any time.'

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('')

const blobToDataUrl = (blob: Blob) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(reader.result as string)
  reader.onerror = () => reject(reader.error)
  reader.readAsDataURL(blob)
})

/** The photo as a data URL, fetched with the user's credentials (the endpoints are never public). */
function usePhotoUrl(source: PhotoSource): string | null {
  const { data } = useQuery({
    queryKey: ['person-photo', source.key, source.version],
    queryFn: async () => blobToDataUrl(await source.load()),
    enabled: source.enabled && !!source.version,
    staleTime: Infinity,
    gcTime: 10 * 60 * 1000,
    retry: false,
  })
  return data ?? null
}

/** A small round photo (or initials). Click to view, or to add/replace/remove when editing is allowed. */
export function PhotoAvatar({ source, size = 32 }: { source: PhotoSource; size?: number }) {
  const [open, setOpen] = useState(false)
  const url = usePhotoUrl(source)

  if (!source.enabled) return null
  const clickable = source.canEdit || !!source.version

  const face = (
    <span
      className="rounded-full bg-gray-100 text-gray-500 flex items-center justify-center overflow-hidden shrink-0 font-medium"
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.36)) }}
    >
      {url
        ? <img src={url} alt={source.name} className="w-full h-full object-cover" />
        : source.canEdit && !source.version
          ? <Camera size={Math.round(size * 0.45)} className="text-gray-400" />
          : initials(source.name)}
    </span>
  )

  return (
    <>
      {clickable ? (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); setOpen(true) }}
          title={source.version ? 'View photo' : 'Add photo'}
          className="cursor-pointer rounded-full ring-offset-1 hover:ring-2 hover:ring-indigo-300 shrink-0"
        >
          {face}
        </button>
      ) : face}
      {open && <PhotoEditor source={source} url={url} onClose={() => setOpen(false)} />}
    </>
  )
}

/** Centre-crops to a square and shrinks to 512px JPEG in the browser (the server does it again). */
async function toSquareJpeg(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  const side = Math.min(bitmap.width, bitmap.height)
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 512
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, 512, 512)
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, 512, 512)
  bitmap.close()
  return new Promise((resolve, reject) =>
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', 0.85))
}

function PhotoEditor({ source, url, onClose }: { source: PhotoSource; url: string | null; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [pending, setPending] = useState<{ blob: Blob; url: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)

  const choose = async (file: File | undefined) => {
    if (!file) return
    setError(null)
    try {
      const blob = await toSquareJpeg(file)
      setPending({ blob, url: await blobToDataUrl(blob) })
    } catch {
      setError("We couldn't read that image. Please use a JPEG or PNG photo.")
    }
  }

  const finish = () => {
    queryClient.invalidateQueries({ queryKey: ['person-photo', source.key] })
    source.onChanged?.()
    onClose()
  }

  const run = async (action: () => Promise<unknown>) => {
    if (guardPreview()) return
    setBusy(true)
    setError(null)
    try {
      await action()
      finish()
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } | string } })?.response?.data
      setError(typeof detail === 'string' ? detail : detail?.detail ?? 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const shown = pending?.url ?? url

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/50" onClick={e => { e.stopPropagation(); onClose() }}>
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-semibold text-gray-900">{source.name}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="w-56 h-56 mx-auto rounded-xl bg-gray-100 overflow-hidden flex items-center justify-center text-gray-400 text-sm">
          {shown ? <img src={shown} alt={source.name} className="w-full h-full object-cover" /> : 'No photo yet'}
        </div>

        {source.canEdit && (
          <>
            <p className="text-xs text-gray-500">{source.notice?.trim() || DEFAULT_NOTICE}</p>

            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => { void choose(e.target.files?.[0]); e.target.value = '' }} />
            <input ref={cameraRef} type="file" accept="image/*" capture="user" className="hidden" onChange={e => { void choose(e.target.files?.[0]); e.target.value = '' }} />

            {pending ? (
              <div className="flex gap-2">
                <Button className="flex-1" disabled={busy} onClick={() => run(() => source.upload(pending.blob))}>
                  {busy ? 'Saving…' : 'Save photo'}
                </Button>
                <Button variant="outline" disabled={busy} onClick={() => setPending(null)}>Cancel</Button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()}>
                  <Upload size={14} /> {source.version ? 'Replace' : 'Upload'}
                </Button>
                <Button variant="outline" className="gap-1.5" onClick={() => cameraRef.current?.click()}>
                  <Camera size={14} /> Take photo
                </Button>
                {source.version && source.remove && (
                  <Button
                    variant="outline"
                    className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50 ml-auto"
                    disabled={busy}
                    onClick={() => { if (confirm('Remove this photo? It will be deleted straight away.')) void run(source.remove!) }}
                  >
                    <Trash2 size={14} /> Remove
                  </Button>
                )}
              </div>
            )}
          </>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </div>
  )
}
